import React, { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';
import { formatSOL, formatSOLAmount } from '../utils/formatSol';

interface Subscription {
    id: string;
    creatorId: string;
    creator: {
        id: string;
        username?: string;
        email?: string;
        avatar?: string;
        monthlyPrice?: number;
        yearlyPrice?: number;
    };
    type: 'monthly' | 'yearly';
    status: 'active' | 'cancelled' | 'expired';
    startDate: string;
    endDate: string;
    price: number;
    nextBillingDate?: string;
}

const SubscriptionsPage: React.FC = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<'active' | 'expired'>('active');
    const { user, token } = useAuth();

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetch(`${API_URL}/api/subscriptions/my-subscriptions`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Subscriptions data:', data);

                // Transform the data to match our interface
                const transformedSubscriptions: Subscription[] = (data.subscriptions || []).map((sub: any) => ({
                    id: sub.id,
                    creatorId: sub.userId,
                    creator: {
                        id: sub.user.id,
                        username: sub.user.username,
                        email: sub.user.email,
                        avatar: sub.user.avatar,
                        monthlyPrice: sub.user.monthlyPrice,
                        yearlyPrice: sub.user.yearlyPrice
                    },
                    type: sub.type === 'annual' ? 'yearly' : sub.type,
                    status: sub.isExpired ? 'expired' : sub.status,
                    startDate: sub.startDate,
                    endDate: sub.endDate,
                    price: sub.amount,
                    nextBillingDate: sub.endDate
                }));

                setSubscriptions(transformedSubscriptions);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to load subscriptions');
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async (subscriptionId: string) => {
        if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

        try {
            console.log('Cancelling subscription:', subscriptionId);
            console.log('API URL:', `${API_URL}/api/subscriptions/${subscriptionId}/cancel`);

            const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}/cancel`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Cancel response status:', response.status);

            if (response.ok) {
                const responseData = await response.json();
                console.log('Cancel response data:', responseData);

                // Update local state
                setSubscriptions(prev =>
                    prev.map(sub =>
                        sub.id === subscriptionId
                            ? { ...sub, status: 'cancelled' as const }
                            : sub
                    )
                );
                setError('');

                // Show success message
                alert('Subscription cancelled successfully!');
            } else {
                const errorData = await response.json();
                console.error('Cancel error response:', errorData);
                setError(errorData.error || `Failed to cancel subscription (${response.status})`);
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            setError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDaysUntilBilling = (dateString: string) => {
        const billingDate = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.ceil((billingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return diffInDays;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <span className="badge bg-success font-xssss">Active</span>;
            case 'cancelled':
                return <span className="badge bg-warning font-xssss">Cancelled</span>;
            case 'expired':
                return <span className="badge bg-danger font-xssss">Expired</span>;
            default:
                return <span className="badge bg-secondary font-xssss">{status}</span>;
        }
    };

    const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
    const expiredSubscriptions = subscriptions.filter(sub => sub.status === 'expired' || sub.status === 'cancelled');

    return (
        <Fragment>
            <div className="container-fluid py-2">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
                            <div className="card-body d-flex align-items-center p-0">
                                <div>
                                    <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">My Subscriptions</h2>
                                    <p className="text-grey-500 font-xsss mb-0">Manage your creator subscriptions</p>
                                </div>
                                <div className="ms-auto d-flex align-items-center">
                                    <div className="bg-lightblue p-3 rounded-3 me-3">
                                        <h5 className="fw-700 font-xs text-current mb-1">
                                            {activeSubscriptions.length}
                                        </h5>
                                        <p className="font-xssss text-grey-500 mb-0">Active</p>
                                    </div>
                                    <div className="bg-lightgreen p-3 rounded-3">
                                        <h5 className="fw-700 font-xs text-success mb-1">
                                            {formatSOL(activeSubscriptions.reduce((sum, sub) => sum + (Number(sub.price) || 0), 0))}
                                        </h5>
                                        <p className="font-xssss text-grey-500 mb-0">Total</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="alert alert-danger font-xss fw-500 mb-3">
                                {error}
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
                            <div className="card-body p-0">
                                <ul className="nav nav-tabs h55 d-flex product-info-tab border-bottom-0 ps-4" role="tablist">
                                    <li className="active list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'active' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('active')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Active ({activeSubscriptions.length})
                                        </a>
                                    </li>
                                    <li className="list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'expired' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('expired')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            History ({expiredSubscriptions.length})
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {loading ? (
                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {activeTab === 'active' && (
                                    <>
                                        {activeSubscriptions.length === 0 ? (
                                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                <div className="text-center p-4">
                                                    <i className="feather-credit-card font-xl text-grey-500 mb-3"></i>
                                                    <h4 className="fw-700 text-grey-900">No active subscriptions</h4>
                                                    <p className="text-grey-500 font-xsss">Subscribe to creators to support their content</p>
                                                </div>
                                            </div>
                                        ) : (
                                            activeSubscriptions.map((subscription) => (
                                                <div key={subscription.id} className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                    <div className="card-body p-0">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center">
                                                                <img
                                                                    src={subscription.creator.avatar || '/assets/images/user.png'}
                                                                    alt="Creator"
                                                                    className="rounded-circle me-3"
                                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                />
                                                                <div>
                                                                    <h4 className="fw-700 font-xsss text-grey-900 mb-1">
                                                                        {subscription.creator.username || subscription.creator.email?.split('@')[0]}
                                                                    </h4>
                                                                    <p className="font-xsss text-grey-500 mb-0">
                                                                        {subscription.type.charAt(0).toUpperCase() + subscription.type.slice(1)} Plan
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                {getStatusBadge(subscription.status)}
                                                                <p className="font-xsss fw-600 text-grey-900 mb-0 mt-1">
                                                                    {formatSOLAmount(subscription.price)} SOL/{subscription.type === 'monthly' ? 'month' : 'year'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="row mt-3">
                                                            <div className="col-md-4">
                                                                <div className="mb-2">
                                                                    <label className="font-xsss fw-600 text-grey-700">Started</label>
                                                                    <p className="font-xsss text-grey-900 mb-0">
                                                                        {formatDate(subscription.startDate)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="mb-2">
                                                                    <label className="font-xsss fw-600 text-grey-700">Next Billing</label>
                                                                    <p className="font-xsss text-grey-900 mb-0">
                                                                        {subscription.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'}
                                                                    </p>
                                                                    {subscription.nextBillingDate && (
                                                                        <p className="font-xssss text-grey-500 mb-0">
                                                                            {getDaysUntilBilling(subscription.nextBillingDate)} days
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="mb-2">
                                                                    <label className="font-xsss fw-600 text-grey-700">Actions</label>
                                                                    <div>
                                                                        <button
                                                                            className="btn bg-danger text-white font-xsss fw-600 p-2 rounded-3"
                                                                            onClick={() => handleCancelSubscription(subscription.id)}
                                                                            disabled={subscription.status !== 'active'}
                                                                        >
                                                                            Cancel Subscription
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </>
                                )}

                                {activeTab === 'expired' && (
                                    <>
                                        {expiredSubscriptions.length === 0 ? (
                                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                <div className="text-center p-4">
                                                    <i className="feather-clock font-xl text-grey-500 mb-3"></i>
                                                    <h4 className="fw-700 text-grey-900">No subscription history</h4>
                                                    <p className="text-grey-500 font-xsss">Your cancelled or expired subscriptions will appear here</p>
                                                </div>
                                            </div>
                                        ) : (
                                            expiredSubscriptions.map((subscription) => (
                                                <div key={subscription.id} className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                    <div className="card-body p-0">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center">
                                                                <img
                                                                    src={subscription.creator.avatar || '/assets/images/user.png'}
                                                                    alt="Creator"
                                                                    className="rounded-circle me-3"
                                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                />
                                                                <div>
                                                                    <h4 className="fw-700 font-xsss text-grey-900 mb-1">
                                                                        {subscription.creator.username || subscription.creator.email?.split('@')[0]}
                                                                    </h4>
                                                                    <p className="font-xsss text-grey-500 mb-0">
                                                                        {subscription.type.charAt(0).toUpperCase() + subscription.type.slice(1)} Plan
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                {getStatusBadge(subscription.status)}
                                                                <p className="font-xsss fw-600 text-grey-900 mb-0 mt-1">
                                                                    {formatSOLAmount(subscription.price)} SOL/{subscription.type === 'monthly' ? 'month' : 'year'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="row mt-3">
                                                            <div className="col-md-6">
                                                                <div className="mb-2">
                                                                    <label className="font-xsss fw-600 text-grey-700">Period</label>
                                                                    <p className="font-xsss text-grey-900 mb-0">
                                                                        {formatDate(subscription.startDate)} - {formatDate(subscription.endDate)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="mb-2">
                                                                    <label className="font-xsss fw-600 text-grey-700">Total Paid</label>
                                                                    <p className="font-xsss text-grey-900 mb-0">
                                                                        {formatSOL(subscription.price)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default SubscriptionsPage;
